import { useMemo } from 'react';

import { Row } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { DocComparerGroup } from './doc-comparer-group';
import {
  getInconsistencySourceDisplayName,
  getInconsistencySourceTooltip,
  getInconsistencyTypeDisplayName,
  getInconsistencyTypeTooltip,
} from '../../../../../backend/src/matter/inconsistency/inconsistency-display';
import { Relevance } from '../../../../../backend/src/types/types';
import { Inconsistency } from '@/api/types/backend-dto-types';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Dialog, DialogContent, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { TextWithReferences } from '@/new/components/text-references';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';

export function InconsistencyCard({
  row,
  inconsistency,
  inInconsistencyDrawer = false,
}: {
  row?: Row<Inconsistency>;
  inconsistency: Inconsistency;
  inInconsistencyDrawer?: boolean;
}) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { openReferenceDrawer } = useStore();

  // TODO: update this to use new subset relevance when available
  const hasRelevantFacts = useMemo(
    () => inconsistency.facts.some((f) => f.fact.relevance === Relevance.Relevant),
    [inconsistency.facts],
  );

  // group facts by 'name' which is the group name, e.g. 'Different evidence...'
  const groupedFacts = useMemo(() => Object.groupBy(inconsistency.facts, ({ name }) => name), [inconsistency.facts]);

  return (
    <AccordionItem value={inconsistency.id} className="w-full px-4 pb-2 not-last:border-b">
      <AccordionTrigger className="w-full pt-1">
        <div className="mr-6 w-full font-normal text-gray-800">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div className="flex items-center gap-2">
              {row && row.getCanSelect() && (
                <Checkbox
                  className="mr-1"
                  onClick={(e) => e.stopPropagation()}
                  checked={row.getIsSelected()}
                  onCheckedChange={row.getToggleSelectedHandler()}
                />
              )}

              <Chip className={`${CHIP_COLOURS.levels[inconsistency.level]} border`}>
                <Icon className={`lucide--shield-alert`} />
                <span>{capitalizeFirstLetter(inconsistency.level)} Inconsistency</span>
              </Chip>

              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" onClick={(e) => e.stopPropagation()} className="py-0.75">
                    <Icon className="lucide--flip-horizontal-2" />
                    <span>Compare Evidence</span>
                  </Button>
                </DialogTrigger>

                <DialogContent
                  className="flex h-9/10 max-h-none w-9/10 max-w-none gap-4 pb-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  {Object.entries(groupedFacts).map(([group, facts]) =>
                    facts ? <DocComparerGroup key={group} title={group} facts={facts} /> : null,
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {!inInconsistencyDrawer && (
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <p className="text-xs whitespace-nowrap text-gray-500">
                  <strong className="text-gray-800">{inconsistency.facts.length}</strong>{' '}
                  {inconsistency.facts.length === 1 ? 'piece' : 'pieces'} of evidence
                </p>

                {hasRelevantFacts && (
                  <Chip className={`${CHIP_COLOURS.hasRelevantFacts}`}>
                    <Icon className="lucide--circle-check" />
                    <span>Contains Relevant Facts</span>
                  </Chip>
                )}

                <Popover>
                  <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                    <Chip className={`${CHIP_COLOURS.inconsistencySource} border`}>
                      <span>{getInconsistencySourceDisplayName(inconsistency.source)}</span>
                    </Chip>
                  </PopoverTrigger>
                  <PopoverContent className="mx-4">
                    {getInconsistencySourceTooltip(inconsistency.source)}
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                    <Chip className={`${CHIP_COLOURS.inconsistencyType} border`}>
                      <span>{getInconsistencyTypeDisplayName(inconsistency.type)}</span>
                    </Chip>
                  </PopoverTrigger>
                  <PopoverContent className="mx-4">{getInconsistencyTypeTooltip(inconsistency.type)}</PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          <TextWithReferences content={inconsistency.description} disableReferenceClick={inInconsistencyDrawer} />
        </div>
      </AccordionTrigger>

      <AccordionContent className="mt-2 mb-6">
        {Object.entries(groupedFacts).map(([group, facts], i) => (
          <div key={group}>
            <div className="relative py-1 pb-0.5 pl-6">
              <div className={`absolute left-0 h-full w-0.75 rounded-full bg-gray-200`} />

              <p className="my-2 font-medium">{group}</p>

              <div className="text-xs">
                {facts?.map((fact) => (
                  <div key={fact.id}>
                    <div className="flex items-center gap-2">
                      {fact.fact.locations.map((location) => (
                        <div className="flex items-center gap-1.5" key={location.id}>
                          <p
                            // <p> with an onClick is not ideal but button can't be a descendant of button
                            className={cn(
                              'cursor-pointer text-blue-600 duration-150 hover:text-blue-700',
                              inInconsistencyDrawer && 'cursor-default',
                            )}
                            onClick={() =>
                              !inInconsistencyDrawer &&
                              openReferenceDrawer({ factId: location.factId, caseId: subsetId })
                            }
                          >
                            {location.doc.file.name}
                          </p>
                          {location.doc.type && <Chip className={`${CHIP_COLOURS.type}`}>{location.doc.type}</Chip>}
                          {location.doc.dateText && (
                            <Chip className={`${CHIP_COLOURS.default}`}>{location.doc.dateText}</Chip>
                          )}
                        </div>
                      ))}
                    </div>

                    <p className="mt-1 mb-3 text-gray-600">{fact.fact.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {i < Object.keys(groupedFacts).length - 1 && <Icon className="lucide--arrow-up-down my-4 pl-16" />}
          </div>
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
